import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { CheckCircle } from 'lucide-react';
import { apiJson } from '../../lib/api';
import { useTimer } from '../../context/TimerContext';
import { PremiumSuccessDialog } from '../../components/Premium/PremiumSuccessDialog';

export function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const visitorId = searchParams.get('visitor_id');
  const { refreshAccountState } = useTimer();
  const [celebrate, setCelebrate] = useState(false);
  const [stillVerifying, setStillVerifying] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!visitorId) {
        console.error('Missing visitor_id in URL parameters');
        navigate('/');
        return;
      }

      // The redirect may land on a different origin than the checkout started
      // on (e.g. a pinned STRIPE_SUCCESS_URL domain). The visitor that paid is
      // the one in the URL — adopt it so premium applies on this device.
      const localVisitorId = localStorage.getItem('visitor_id');
      if (localVisitorId && localVisitorId !== visitorId) {
        localStorage.setItem('visitor_id', visitorId);
      }

      let attempts = 0;
      const maxAttempts = 15;
      const pollInterval = 1500;

      const checkStatus = async (): Promise<boolean> => {
        attempts++;
        if (attempts > maxAttempts) {
          // Don't silently bounce home — the user just paid and deserves to
          // know the payment was received even if confirmation is lagging.
          console.error('Premium status check timed out after maximum attempts.');
          setStillVerifying(true);
          return true;
        }

        try {
          const data = await apiJson<{ isPremium: boolean; userId?: string }>(
            '/api/premium/status',
            {
              method: 'POST',
              body: JSON.stringify({ visitor_id: visitorId }),
            },
          );

          if (data.isPremium && data.userId) {
            localStorage.setItem('is_premium', 'true');
            localStorage.setItem('user_id', data.userId);
            await refreshAccountState();
            // Celebrate here instead of silently navigating — the user just
            // paid, and the dialog also reminds them they can log in later.
            if (attempts > 1) {
              // Webhook needed a moment; brief pause so the moment lands.
              await new Promise((resolve) => setTimeout(resolve, 600));
            }
            setCelebrate(true);
            return true;
          }

          return false;
        } catch (err) {
          console.error(`Attempt ${attempts}: Premium status check failed:`, err);
          return false;
        }
      };

      const initiallySubscribed = await checkStatus();
      if (initiallySubscribed) {
        return;
      }

      const pollTimer = setInterval(async () => {
        const isSubscribed = await checkStatus();
        if (isSubscribed) {
          clearInterval(pollTimer);
        }
      }, pollInterval);

      return () => clearInterval(pollTimer);
    };

    checkSubscription();
  }, [navigate, refreshAccountState, visitorId]);

  if (celebrate) {
    return <PremiumSuccessDialog onDone={() => navigate('/')} />;
  }

  if (stillVerifying) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mb-4 mx-auto" />
          <h1 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
            Payment received!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            We're still confirming it with the payment provider — this usually
            takes just a moment. Refresh in a few seconds and your premium
            features will be unlocked.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
          >
            Back to the timer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-indigo-500 animate-spin" />
        <h1 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
          Confirming your payment…
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Verifying account status. You'll be redirected shortly...
        </p>
      </div>
    </div>
  );
}
